import type { AppStatus } from "#shared/schemas/appStatus.ts";
import type { MbApiLogEntry } from "#shared/schemas/mbApiLog.ts";
import type { SpotifyApiLogEntry } from "#shared/schemas/spotifyApiLog.ts";

const STYLE =
  "color:#1DB954;font-weight:600;font-family:ui-monospace,SFMono-Regular,Menlo,monospace";
const MUTED = "color:#aaa;font-family:ui-monospace,SFMono-Regular,Menlo,monospace";
const WARN = "color:#f0a500;font-weight:600;font-family:ui-monospace,SFMono-Regular,Menlo,monospace";
const ERR = "color:#e91429;font-weight:600;font-family:ui-monospace,SFMono-Regular,Menlo,monospace";

/** F12 console helpers — filter DevTools by `coykoi`. */
export const coyLog = {
  info(message: string, detail?: unknown): void {
    if (detail !== undefined) console.log(`%c[coykoi] %c${message}`, STYLE, MUTED, detail);
    else console.log(`%c[coykoi] %c${message}`, STYLE, MUTED);
  },
  warn(message: string, detail?: unknown): void {
    if (detail !== undefined) console.log(`%c[coykoi] %c${message}`, WARN, MUTED, detail);
    else console.log(`%c[coykoi] %c${message}`, WARN, MUTED);
  },
  error(message: string, detail?: unknown): void {
    if (detail !== undefined) console.log(`%c[coykoi] %c${message}`, ERR, MUTED, detail);
    else console.log(`%c[coykoi] %c${message}`, ERR, MUTED);
  },
  api(entry: SpotifyApiLogEntry): void {
    const label = entry.label ? ` (${entry.label})` : "";
    const ra = entry.retry_after != null ? ` retry-after=${entry.retry_after}s` : "";
    const line =
      `#${entry.seq} ${entry.method} ${entry.path} → ${entry.status} ${entry.ms}ms` +
      `${label}${ra} · today web=${entry.kind === "web_api" ? "yes" : "token"} day#${entry.day_count}`;
    if (entry.quota_exceeded || entry.status === 429) {
      console.log(`%c[coykoi:api] %c${line}`, WARN, MUTED, entry);
    } else if (!entry.ok) {
      console.log(`%c[coykoi:api] %c${line}`, ERR, MUTED, entry);
    } else {
      console.log(`%c[coykoi:api] %c${line}`, STYLE, MUTED);
    }
  },
  mb(entry: MbApiLogEntry): void {
    const label = entry.label ? ` (${entry.label})` : "";
    const ra = entry.retry_after != null ? ` retry-after=${entry.retry_after}s` : "";
    const line =
      `#${entry.seq} ${entry.method} ${entry.path} → ${entry.status} ${entry.ms}ms` +
      `${label}${ra} · today=${entry.day_count}`;
    if (entry.status === 503 || entry.status === 429) {
      console.log(`%c[coykoi:mb] %c${line}`, WARN, MUTED, entry);
    } else if (!entry.ok) {
      console.log(`%c[coykoi:mb] %c${line}`, ERR, MUTED, entry);
    } else {
      console.log(`%c[coykoi:mb] %c${line}`, STYLE, MUTED);
    }
  },
};

export function logAppStatusSnapshot(status: AppStatus, reason: string): void {
  const who = status.user
    ? `${status.user.display_name ?? "(no name)"} · id=${status.user.id}` +
      (status.user.email ? ` · ${status.user.email}` : "")
    : "nobody";
  const you = status.completeness;

  coyLog.info(`status (${reason})`, {
    connected: status.connected,
    api_ok: status.api_ok,
    account: who,
    your_library: {
      liked: you.liked,
      playlists: you.playlists_listed,
      with_tracks: you.playlists_with_tracks,
      empty: you.playlists_empty,
      unique_tracks: you.unique_tracks,
      albums: you.unique_albums,
      artists: you.unique_artists,
    },
    sync: status.sync
      ? `${status.sync.status} · ${status.sync.phase} · ${status.sync.api_calls} calls`
      : "none — run Sync library",
    quota: status.quota.active
      ? `PAUSED ${status.quota.reason} · ${status.quota.remaining_sec}s left`
      : "clear",
    api_today: `${status.api_today.web_api_calls} web / ${status.api_today.calls} total (${status.api_today.day})`,
    error: status.error,
  });
}
